/**
 * Pure helpers for the public URL slug fields (Company, CMSProduct, CMSNews,
 * Lecture, CMSJobListing, Event, MediaSeries, MediaItem). Used by
 * components/toolbox/SlugField.jsx and by the edit forms when they build
 * their mutation input.
 *
 * Kept free of React and Amplify so it can be tested with node:test, see
 * scripts/slug-frontend/slugInput.test.js.
 */
import { makeUniqueSlug, normalizeSlug, pickSourceText, slugify } from './slugify.js';

/**
 * Per entity: the generated bySlug query (src/graphql/queries.js), the public
 * website path the slug ends up in and the field holding the display name.
 *
 * `scopeField` (optional): the slug is only unique within the value of this
 * field (MediaItem slugs are unique per series). The lookup then also selects
 * the field and findSlugOwner ignores owners from other scopes.
 */
export const SLUG_ENTITIES = {
  company: { queryName: 'companiesBySlug', pathPrefix: '/unternehmensseite/', nameField: 'name', nameIsMultiLang: false },
  product: { queryName: 'cmsProductsBySlug', pathPrefix: '/product/', nameField: 'name', nameIsMultiLang: true },
  news: { queryName: 'cmsNewsBySlug', pathPrefix: '/news-article/', nameField: 'name', nameIsMultiLang: true },
  lecture: { queryName: 'lecturesBySlug', pathPrefix: '/lecture/', nameField: 'title', nameIsMultiLang: true },
  job: { queryName: 'cmsJobListingsBySlug', pathPrefix: '/job-listing/', nameField: 'name', nameIsMultiLang: true },
  event: { queryName: 'eventsBySlug', pathPrefix: '/', nameField: 'name', nameIsMultiLang: true },
  mediaSeries: { queryName: 'mediaSeriesBySlug', pathPrefix: '/mediathek/', nameField: 'title', nameIsMultiLang: true },
  mediaItem: { queryName: 'mediaItemsBySlug', pathPrefix: '/mediathek/', nameField: 'title', nameIsMultiLang: true, scopeField: 'mediaSeriesId' },
};

/**
 * Minimal GraphQL document for the uniqueness check. It uses the same query
 * name and arguments as the generated document in src/graphql/queries.js but
 * only selects id + display name (+ the scope field for scoped entities), so
 * nested fields with stricter field auth (e.g. Company.admin) cannot make the
 * lookup fail.
 * @returns {string|null} null for an unknown entity
 */
export function slugLookupQuery(entity) {
  const cfg = SLUG_ENTITIES[entity];
  if (!cfg) return null;
  const nameSelection = cfg.nameIsMultiLang ? `${cfg.nameField} { language text }` : cfg.nameField;
  const scopeSelection = cfg.scopeField ? `\n      ${cfg.scopeField}` : '';
  return `query SlugLookup($slug: String!, $limit: Int) {
  ${cfg.queryName}(slug: $slug, limit: $limit) {
    items {
      id
      ${nameSelection}${scopeSelection}
    }
  }
}`;
}

/** Items array from a client.graphql() result (or from a rejected GraphQLResult carrying partial data). */
export function extractSlugLookupItems(response, entity) {
  const cfg = SLUG_ENTITIES[entity];
  if (!cfg) return null;
  const items = response?.data?.[cfg.queryName]?.items;
  return Array.isArray(items) ? items : null;
}

/**
 * The first item that is not the entity being edited, or null when the slug
 * is free (or only used by the entity itself).
 *
 * For scoped entities (SLUG_ENTITIES[entity].scopeField) items from another
 * scope are ignored when `scopeValue` is given; without a scopeValue every
 * other item counts (conservative).
 * @returns {{id: string, name: string}|null}
 */
export function findSlugOwner(items, entityId, entity, scopeValue) {
  if (!Array.isArray(items)) return null;
  const cfg = SLUG_ENTITIES[entity];
  const scopeField = cfg?.scopeField && scopeValue ? cfg.scopeField : null;
  const other = items.find((item) => item && item.id && item.id !== entityId
    && (!scopeField || item[scopeField] === scopeValue));
  if (!other) return null;
  const name = pickSourceText(other[cfg?.nameField || 'name']) || other.id;
  return { id: other.id, name };
}

/**
 * Public website path for a slug, '' when either is unknown/empty.
 * `pathPrefixOverride` replaces the entity's pathPrefix (e.g. "/mediathek/<series>/").
 */
export function publicPathForSlug(entity, slug, pathPrefixOverride) {
  const cfg = SLUG_ENTITIES[entity];
  if (!cfg || !slug) return '';
  const prefix = typeof pathPrefixOverride === 'string' && pathPrefixOverride ? pathPrefixOverride : cfg.pathPrefix;
  return prefix + slug;
}

/**
 * The `slugHistory` part of a mutation input for entities whose history is
 * maintained by the admin form (Mediathek; there is no stream lambda):
 * {} when the slug did not change or no slug was loaded, otherwise the loaded
 * history plus the loaded slug (deduplicated, without the new slug).
 *
 * @param {string} newSlug slug that is about to be saved
 * @param {string|null|undefined} loadedSlug slug as loaded with the entity
 * @param {string[]|null|undefined} loadedHistory slugHistory as loaded with the entity
 */
export function slugHistoryMutationInput(newSlug, loadedSlug, loadedHistory) {
  const next = typeof newSlug === 'string' ? newSlug.trim() : '';
  const loaded = typeof loadedSlug === 'string' ? loadedSlug.trim() : '';
  if (!loaded || loaded === next) return {};
  const previous = Array.isArray(loadedHistory)
    ? loadedHistory.filter((s) => typeof s === 'string' && s.trim()).map((s) => s.trim())
    : [];
  const slugHistory = [...new Set([...previous, loaded])].filter((s) => s !== next);
  return { slugHistory };
}

/**
 * Decides the slug to save for entities whose slug must never be empty and
 * is maintained by the admin form (MediaSeries, MediaItem). Never returns an
 * empty slug and never null.
 *
 * - An untouched loaded slug is kept as is (legacy spellings are not rewritten),
 *   unless `recheck` is set (e.g. a MediaItem moved to another series) - then it
 *   is checked for conflicts like an edited slug.
 * - An edited slug is normalized (normalizeSlug) and checked: `conflict` is true
 *   when isTaken() reports it as used by another entity.
 * - An empty value is generated from `sourceText` (fallback: `fallback`) and
 *   made unique with -2, -3, ... (makeUniqueSlug).
 *
 * @param {string|null|undefined} value current form value
 * @param {{loadedSlug?: string|null, sourceText?: string, fallback?: string,
 *   recheck?: boolean, isTaken: (slug: string) => Promise<boolean>|boolean}} options
 * @returns {Promise<{slug: string, generated: boolean, changed: boolean, conflict: boolean}>}
 */
export async function resolveRequiredSlug(value, { loadedSlug, sourceText, fallback = 'item', recheck = false, isTaken }) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const loaded = typeof loadedSlug === 'string' ? loadedSlug.trim() : '';
  const check = async (slug) => (typeof isTaken === 'function' ? Boolean(await isTaken(slug)) : false);

  if (raw && raw === loaded) {
    return { slug: loaded, generated: false, changed: false, conflict: recheck ? await check(loaded) : false };
  }
  const normalized = normalizeSlug(raw);
  if (normalized) {
    const changed = normalized !== loaded;
    const conflict = changed || recheck ? await check(normalized) : false;
    return { slug: normalized, generated: false, changed, conflict };
  }
  const base = slugify(sourceText || '') || slugify(fallback) || 'item';
  const slug = await makeUniqueSlug(base, check);
  return { slug, generated: true, changed: slug !== loaded, conflict: false };
}

/**
 * The `slug` part of a create/update mutation input, to be spread into the
 * input object.
 *
 * - Users outside Admin/Moderator get {} : AppSync field auth rejects the whole
 *   mutation when their input contains "slug".
 * - The form value is normalized (normalizeSlug) before it is compared and sent.
 * - Create: a non-empty slug is sent, an empty one is omitted (the backend
 *   generates one).
 * - Update: "slug" is only included when the normalized value differs from the
 *   normalized slug loaded with the entity (loadedSlug), so an untouched or
 *   stale form value never overwrites what the backend holds. Clearing a
 *   previously non-empty slug sends null, which removes the attribute so the
 *   backend regenerates it; empty before and after sends nothing.
 *
 * @param {string|null|undefined} slug current form value
 * @param {{canEditSlug: boolean, isUpdate: boolean, loadedSlug?: string|null}} options
 *   loadedSlug: the slug as fetched with the entity (only used on update)
 */
export function slugMutationInput(slug, { canEditSlug, isUpdate, loadedSlug }) {
  if (!canEditSlug) return {};
  const raw = typeof slug === 'string' ? slug.trim() : '';
  const value = normalizeSlug(raw);
  if (!isUpdate) return value ? { slug: value } : {};
  // Compare against the slug as loaded (not normalized): an untouched legacy slug such as
  // "pumpe²" is left alone, while a value the admin edited or the field canonicalised on
  // blur is sent, and clearing a previously set slug sends null (backend regenerates).
  const loaded = typeof loadedSlug === 'string' ? loadedSlug.trim() : '';
  if (raw === loaded || value === loaded) return {};
  return { slug: value || null };
}
